import { useState, useEffect } from 'react';
import axios from 'axios';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const SplashScreenAdmin = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { user } = useSelector((state) => state.user);
    const canEdit = user.role === "admin" || user.adminRole?.permissions?.splashScreen?.edit;

    const [customerSplash, setCustomerSplash] = useState("");
    const [customerSplashPreview, setCustomerSplashPreview] = useState("");
    const [driverSplash, setDriverSplash] = useState("");
    const [driverSplashPreview, setDriverSplashPreview] = useState("");

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/splash-settings');
                setCustomerSplashPreview(data.settings.customerAppSplash?.url || "");
                setDriverSplashPreview(data.settings.driverAppSplash?.url || "");
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load splash settings", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFileChange = (setPreview, setValue) => (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setPreview(reader.result);
                setValue(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {};
            if (customerSplash) payload.customerAppSplash = customerSplash;
            if (driverSplash) payload.driverAppSplash = driverSplash;

            if (!payload.customerAppSplash && !payload.driverAppSplash) {
                enqueueSnackbar("Choose at least one new image first", { variant: "warning" });
                setSaving(false);
                return;
            }

            await axios.put('/api/v1/admin/splash-settings', payload);
            enqueueSnackbar("Splash screens updated", { variant: "success" });
            setCustomerSplash("");
            setDriverSplash("");
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Splash Screens | 1313 Mart" />
            {saving && <BackdropLoader />}

            <div className="flex flex-col gap-4">
                <h1 className="text-lg font-medium uppercase">Splash Screens</h1>

                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-lg p-4">
                    <strong>Heads up:</strong> uploading here doesn't update apps already installed on phones.
                    A splash screen is baked into the app at build time. After uploading here, download the
                    image, save it into the app's <code>assets</code> folder as <code>splash-background.png</code>,
                    then run a new EAS build for that app to actually see it.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Customer App */}
                    <div className="bg-white rounded-lg shadow p-5 flex flex-col gap-3">
                        <h2 className="font-medium text-gray-800">Customer App</h2>
                        <div className="w-40 aspect-[9/19] mx-auto flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!customerSplashPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={customerSplashPreview} alt="Customer App Splash" className="w-full h-full object-cover" />
                            }
                        </div>
                        {canEdit && (
                            <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg text-sm">
                                <input type="file" accept="image/*" onChange={handleFileChange(setCustomerSplashPreview, setCustomerSplash)} className="hidden" />
                                Choose New Image
                            </label>
                        )}
                        {customerSplashPreview && (
                            <a
                                href={customerSplashPreview}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 text-primary-blue text-sm hover:underline"
                            >
                                <DownloadIcon sx={{ fontSize: "18px" }} /> Open current image (right-click → Save As)
                            </a>
                        )}
                    </div>

                    {/* Driver App */}
                    <div className="bg-white rounded-lg shadow p-5 flex flex-col gap-3">
                        <h2 className="font-medium text-gray-800">Driver App</h2>
                        <div className="w-40 aspect-[9/19] mx-auto flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!driverSplashPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={driverSplashPreview} alt="Driver App Splash" className="w-full h-full object-cover" />
                            }
                        </div>
                        {canEdit && (
                            <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg text-sm">
                                <input type="file" accept="image/*" onChange={handleFileChange(setDriverSplashPreview, setDriverSplash)} className="hidden" />
                                Choose New Image
                            </label>
                        )}
                        {driverSplashPreview && (
                            <a
                                href={driverSplashPreview}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 text-primary-blue text-sm hover:underline"
                            >
                                <DownloadIcon sx={{ fontSize: "18px" }} /> Open current image (right-click → Save As)
                            </a>
                        )}
                    </div>
                </div>

                {canEdit && (
                    <div className="flex justify-end">
                        <button onClick={handleSave} className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg">
                            Save Changes
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default SplashScreenAdmin;